import { AxiosError, AxiosResponse } from "axios";
import toast from "react-hot-toast";

import { ApiResult } from "@/types/api-response";
import { config } from "@/config/base";

type MiddlewareCallback<Data> = (data: ApiResult<Data>) => void | Promise<void>;

let isNetworkErrorShown = false;

const showNetworkError = () => {
  if (isNetworkErrorShown) return;

  isNetworkErrorShown = true;

  toast.error(config.appConfig.errors.serverConnection, {
    id: "network-error",
    duration: 4000
  });

  setTimeout(() => {
    isNetworkErrorShown = false;
  }, 3000);
};

export const apiResponseMiddleware = async <Data>(
  apiCall: Promise<AxiosResponse<ApiResult<Data>>>,
  onSuccess: MiddlewareCallback<Data>,
  options?: {
    showToast?: boolean;
    loadingMessage?: string;
  }
): Promise<ApiResult<Data>> => {
  // ذخیره مقدار showToast برای استفاده در کل تابع
  const shouldShowToast = options?.showToast !== false;

  if (shouldShowToast) {
    toast.loading(options?.loadingMessage || config.appConfig.errors.loadingData, {
      id: "api-loading"
    });
  }

  try {
    const response = await apiCall;

    const { statusCode, message } = response.data;

    if (shouldShowToast) {
      if (statusCode === 300) {
        toast.success(message || config.appConfig.errors.success, {
          id: "api-loading"
        });
      } else if (statusCode === 301) {
        toast(message || "توجه: عملیات با هشدار انجام شد", {
          icon: "⚠️",
          id: "api-loading"
        });
      } else if (statusCode === 302) {
        toast.error(message || config.appConfig.errors.generalError, {
          id: "api-loading"
        });
      } else {
        toast.success(message || config.appConfig.errors.success, {
          id: "api-loading"
        });
      }
    }

    await onSuccess(response.data);

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;

    // 🌐 Network / Offline error
    if (!axiosError.response || !navigator.onLine) {
      showNetworkError();
      throw error;
    }

    // فقط اگر shouldShowToast فعال باشد، toast خطا نمایش بده
    if (shouldShowToast) {
      const status = axiosError.response?.status;
      const message = (axiosError.response?.data as any)?.Message;
      if (status === 403) {
        toast.error(message || config.appConfig.errors.serverError, {
          id: "api-error"
        });
      } else if (status === 503) {
        window.location.href = "/dashboard/error";
      } else if (status && status >= 500) {
        // اگر پیام مشخصی از سرور آمده، همان را نشان بده
        toast.error(message || config.appConfig.errors.serverError, {
          id: "api-error"
        });
      } else if (status && status >= 400) {
        toast.error(message || config.appConfig.errors.requestError, {
          id: "api-error"
        });
      } else {
        toast.error(config.appConfig.errors.unexpectedError, {
          id: "api-error"
        });
      }
    }

    if (shouldShowToast) throw error;
    else return null as any; // یا می‌توانید یک مقدار پیش‌فرض مناسب برگردانید

  }
};